import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Zap, Loader2, CreditCard, Briefcase, Sparkles, Users } from "lucide-react";
import { getBilling, changePlan, buyCredits } from "@/lib/companyApi";
import { completePurchase } from "@/lib/razorpay";

const PLAN_FEATURES = {
  free: ["1 active job", "Applicant pipeline", "Basic dashboard"],
  growth: ["5 active jobs", "5 screening credits / mo", "Talent Pool access", "Pre-apply screening interview"],
  scale: ["20 active jobs", "25 screening credits / mo", "Talent Pool access", "Pre-apply screening interview", "Priority support"],
};

export default function Billing() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    try { const { data } = await getBilling(); setData(data); } catch (_) {}
  };
  useEffect(() => { load(); }, []);

  const onChangePlan = async (plan) => {
    setBusy(`plan-${plan}`); setMsg(null);
    try {
      const { data } = await changePlan(plan);
      const r = await completePurchase(data); // Razorpay when live, instant in mock mode
      setMsg(`Switched to ${plan} — ${r.granted_credits} credits added.`);
      await load();
    } catch (e) { setMsg(e?.message || e?.response?.data?.detail || "Could not change plan."); }
    finally { setBusy(null); }
  };
  const onBuy = async (credits) => {
    setBusy(`buy-${credits}`); setMsg(null);
    try {
      const { data } = await buyCredits(credits);
      const r = await completePurchase(data); // Razorpay when live, instant in mock mode
      setMsg(`${credits} credits added. Balance: ${r.credits}.`);
      await load();
    } catch (e) { setMsg(e?.message || e?.response?.data?.detail || "Could not buy credits."); }
    finally { setBusy(null); }
  };

  if (!data) {
    return <div className="container-x py-16 text-stone-500 flex items-center gap-2"><Loader2 size={18} className="animate-spin" /> Loading billing…</div>;
  }

  const { plan, credits, usage, plans, credit_packs } = data;

  return (
    <div className="container-x py-10 md:py-14">
      <div className="mb-8">
        <span className="overline">Billing</span>
        <h1 className="font-display text-4xl md:text-5xl mt-2">Plans & credits</h1>
        <p className="text-stone-500 mt-2">Upgrade for more job slots, screening, and Talent Pool access.</p>
      </div>

      {/* Current status */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
        <StatCard icon={Sparkles} label="Current plan" value={plans.find(p => p.id === plan)?.name || plan} accent="#C2410C" />
        <StatCard icon={CreditCard} label="Credits" value={credits} accent="#7C3AED" sub="1 credit = 1 screening job" />
        <StatCard icon={Briefcase} label="Active jobs" value={`${usage.active_jobs} / ${usage.job_slots}`} accent="#15803D" />
      </div>

      {msg && <p className="mb-6 text-sm rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-2.5">{msg}</p>}

      {/* Plans */}
      <h2 className="font-display text-2xl mb-4">Choose a plan</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
        {plans.map((p, i) => {
          const current = p.id === plan;
          const highlight = p.id === "growth";
          return (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
              className={`relative rounded-3xl border p-7 flex flex-col ${highlight ? "border-[#C2410C] shadow-[0_24px_60px_-30px_rgba(194,65,12,0.4)]" : "border-stone-200"} bg-white`}
            >
              {highlight && <span className="absolute -top-3 left-7 text-[10px] font-mono uppercase tracking-widest bg-[#C2410C] text-white px-2.5 py-1 rounded-full">Most popular</span>}
              <h3 className="font-display text-2xl">{p.name}</h3>
              <p className="text-sm text-stone-500 mt-1">{p.tagline}</p>
              <p className="mt-4 font-display text-4xl">
                {p.price_inr === 0 ? "Free" : <>₹{p.price_inr.toLocaleString("en-IN")}<span className="text-base text-stone-400"> /mo</span></>}
              </p>
              <ul className="mt-5 space-y-2 flex-1">
                {(PLAN_FEATURES[p.id] || []).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-stone-700">
                    <Check size={15} className="text-emerald-600 shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              <button
                disabled={current || busy === `plan-${p.id}`}
                onClick={() => onChangePlan(p.id)}
                data-testid={`plan-${p.id}`}
                className={`mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-medium transition-colors disabled:opacity-60 ${
                  current ? "bg-stone-100 text-stone-500 cursor-default"
                  : highlight ? "bg-[#C2410C] text-white hover:bg-[#9a3412]"
                  : "bg-stone-900 text-white hover:bg-[#C2410C]"
                }`}
              >
                {busy === `plan-${p.id}` ? <Loader2 size={15} className="animate-spin" /> : current ? "Current plan" : `Switch to ${p.name}`}
              </button>
            </motion.div>
          );
        })}
      </div>

      {/* Credit packs */}
      <h2 className="font-display text-2xl mb-2">Buy credits</h2>
      <p className="text-stone-500 text-sm mb-5">Top up screening credits anytime — each credit enables the pre-apply interview on one job.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {credit_packs.map((pk) => (
          <div key={pk.credits} className="rounded-2xl border border-stone-200 bg-white p-6 flex items-center justify-between">
            <div>
              <p className="font-display text-2xl">{pk.credits} credits</p>
              <p className="text-sm text-stone-500">₹{pk.price_inr.toLocaleString("en-IN")}</p>
            </div>
            <button
              onClick={() => onBuy(pk.credits)}
              disabled={busy === `buy-${pk.credits}`}
              data-testid={`buy-${pk.credits}`}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-stone-900 text-white text-sm hover:bg-[#C2410C] transition-colors disabled:opacity-60"
            >
              {busy === `buy-${pk.credits}` ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />} Buy
            </button>
          </div>
        ))}
      </div>
      <p className="mt-6 text-xs text-stone-400">Payments are mocked in this build — purchases adjust your balance instantly. Wire a payment gateway before launch.</p>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent, sub }) {
  return (
    <div className="rounded-3xl border border-stone-200 bg-white p-6">
      <span className="inline-flex p-2.5 rounded-xl mb-4" style={{ background: `${accent}15`, color: accent }}><Icon size={20} /></span>
      <p className="font-display text-3xl text-stone-900">{value}</p>
      <p className="overline mt-1">{label}</p>
      {sub && <p className="text-xs text-stone-400 mt-1">{sub}</p>}
    </div>
  );
}
